import React, { useState, useContext } from 'react';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import {TextField, InputAdornment, useMediaQuery} from '@mui/material';
import Link from '@mui/material/Link';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { ThemeProvider } from '@mui/material/styles';
import nbxTheme from '../themes/nbxTheme';
import useEmailValidation from '../hooks/useEmailValidation';
import WhmcsApi from '../../services/WhmcsApi'
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2'
import { AuthContext } from '../../Auth';
import useFormValidation from '../hooks/useFormValidation';
import interrogationImg from '../../images/interrogation.svg'
import consejosImg from '../../images/consejos.svg'
import consejosHoverImg from '../../images/consejos_color.svg'
import PasswordStrengthBar from './PasswordStrengthBar';
import { useTranslation } from 'react-i18next';
import Cookies from 'universal-cookie';

export default function SignUp({ setValue, onOpenLogin, onCloseRegister, source}) {
    const esMovil = useMediaQuery('(max-width:767px)');
    const { setUserAuth } = useContext(AuthContext);

    const [hover, setHover] = useState(false);
    const { t } = useTranslation();

    const { email, emailError, setEmailError, handleEmailChange } = useEmailValidation();
    const { values, errors, handleChange, validate } = useFormValidation({
      firstname: '',
      lastname: '',
      password: '',
      passwordrepeat: ''
    }); 
    const [loading, setLoading] = useState(false);
    let { firstname, lastname, password, passwordrepeat } = values;

    const handleSubmit = (event) => {
      event.preventDefault();
      if(email == ''){
        setEmailError(t("signUp.validate.empty.default"))
      }
      //Valida todos los campos y arroja error en sweetalert
      if (!validate() || !!emailError) {
        let errorDescription = t("signUp.validate.verifyText")
          Swal.fire({
            title: 'Error!',
            text: errorDescription,
            icon: 'error',
            confirmButtonText: 'OK'
          })
        return;
      }

        if (validatePasswords(password, passwordrepeat)){
            setLoading(true)
        } else {
            setLoading(false)
            return null
        }
    
      const fetchRegister = async () => {
        try {
          const whmcsApi = new WhmcsApi();
          const response = await whmcsApi.request('register', {
            firstname: firstname,
            lastname: lastname,
            email: email,
            password: password,
          });
          if (response.result === 'error') {
            Swal.fire({
              title: 'Error!',
              text: response.message,
              icon: 'error',
              confirmButtonText: 'OK'
            });
          } else if (response.result === 'success') {
            Swal.fire({
              title: 'Éxito!',
              text: 'Usuario registrado correctamente!',
              icon: 'success',
              confirmButtonText: 'OK'
            });
            const cookieDomain = process.env.REACT_APP_COOKIE_DOMAIN;

            const cookies = new Cookies(null, { path: '/', domain: cookieDomain});
            cookies.set('WHMCSUser', response.data.session);
            setUserAuth(response.data);

            if(source == 'modal'){
              onCloseRegister();
            }
          }
        } catch (error) {
          Swal.fire({
            title: 'Error!',
            text: error.message,
            icon: 'error',
            confirmButtonText: 'OK'
          });
          setLoading(false);
          console.error('Failed to fetch login form:', error);
        } finally {
          setLoading(false);
        }
      };
  
      fetchRegister();
    };
  
    function validatePasswords(password, passwordrepeat) {
        if (password !== passwordrepeat) {
            let errorDescription = t("signUp.validate.notMatch")
            Swal.fire({
                title: 'Error!',
                text: errorDescription,
                icon: 'error',
                confirmButtonText: 'OK'
              })
        } else {
            return true
        }
    }
    
    //Ir a la pestaña de login
    const handleClick = (event) => {
      event.preventDefault();
      setValue(0);
    };
  
    return (
      <ThemeProvider theme={nbxTheme}>
        <Container component="main">
          <CssBaseline />
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <div className='disclaimer'>
                <img style={{marginBottom: 32}} src={interrogationImg} alt="question icon" height="14px" width="14px"/>
                <p dangerouslySetInnerHTML={{ __html: t("signUp.warningText") }} />
            </div>
            <Box component="form" noValidate onSubmit={handleSubmit} sx={{ mt: 3 }} style={{maxWidth: '100%'}}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    autoComplete="given-name"
                    name="firstname"
                    fullWidth
                    id="firstname"
                    label={t("signUp.fields.name")}
                    autoFocus
                    value={values.firstname}
                    onChange={handleChange}
                    error={!!errors.firstname}
                    helperText={errors.firstname}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    id="lastname"
                    label={t("signUp.fields.lastName")}
                    name="lastname"
                    autoComplete="family-name"
                    value={values.lastname}
                    onChange={handleChange}
                    error={!!errors.lastname}
                    helperText={errors.lastname}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    id="email"
                    label={t("signUp.fields.email")}
                    name="email"
                    autoComplete="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    error={!!emailError}
                    helperText={emailError}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="password"
                    label={t("signUp.fields.password")}
                    type="password"
                    id="password"
                    autoComplete="new-password"
                    value={values.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Box
                            display="flex"
                            alignItems="center"
                            position="relative"
                            onMouseEnter={() => setHover(true)}
                            onMouseLeave={() => setHover(false)}
                            sx={{ cursor: 'pointer' }}
                          >
                            <img 
                              src={hover ? consejosHoverImg : consejosImg} 
                              alt="consejos utiles" 
                              style={{ height: '34px', width: '29px' }} 
                            />
                            {hover && (
                              <Box
                                position="absolute"
                                top="40px"
                                transform="translateX(-50%)"
                                bgcolor="#E7F4FC"
                                color="#64748B"
                                p={2}
                                borderRadius={2}
                                sx={{ 
                                  whiteSpace: 'nowrap',
                                  zIndex: 10, 
                                  boxShadow: '0px 4px 40px 0px #437EF74D',
                                  borderRadius: '12px 0 12px 12px',
                                  right: esMovil ? '-40px' : '50%'
                                }}
                              >
                              <div>
                                <p className='tooltip-title'>{t("signUp.tooltip.title")}</p>
                                <div className='tooltip-description' dangerouslySetInnerHTML={{ __html: t("signUp.tooltip.description1") }} />
                                <div className='tooltip-description' dangerouslySetInnerHTML={{ __html: t("signUp.tooltip.description2") }} />
                                <div className='tooltip-description'>{t("signUp.tooltip.description3")}</div>
                              </div>
                              </Box>
                            )}
                          </Box>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <PasswordStrengthBar password={password} />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    name="passwordrepeat"
                    label={t("signUp.fields.passwordRepeat")}
                    type="password"
                    id="passwordrepeat"
                    autoComplete="new-password"
                    value={values.passwordrepeat}
                    onChange={handleChange}
                    error={!!errors.passwordrepeat}
                    helperText={errors.passwordrepeat}
                  />
                </Grid>
              </Grid>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                disabled={loading}
                sx={{ mt: 3, mb: 2, textTransform: 'none', fontFamily: 'Patua One, sans-serif', fontSize: '18px', borderRadius: '10px', height: '60px' }}
              >
                {loading ? <CircularProgress /> : t("signUp.createAccount")}
              </Button>
              <Grid container sx={{ justifyContent: 'center', alignItems: 'center', alignContent: 'center' }}>
                <Grid item>
                  <div style={{ color: '#A0A8B5' }} className='roboto'>{t("signUp.haveAccount")}</div>
                </Grid>
                <Grid item>
                  {source != 'modal' &&
                    <Link style={{ fontSize: '16px', cursor: 'pointer',  marginLeft: 16 }} onClick={handleClick} variant="body2">
                      {t("signUp.signHere")}
                    </Link>
                  }
                  {source == 'modal' &&
                    <Link style={{ fontSize: '16px', cursor: 'pointer',  marginLeft: 16 }}  onClick={onOpenLogin} variant="body2">
                      {t("signUp.signHere")}
                    </Link>
                  }
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Container>
      </ThemeProvider>
    );
  }